import React, { useEffect, useState } from "react";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@/components/ui/card";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table";
import {
	BarChart,
	Bar,
	XAxis,
	YAxis,
	Tooltip,
	ResponsiveContainer,
	CartesianGrid,
} from "recharts";
import { Loader2, ArrowUpDown, ArrowUp, ArrowDown, Filter, X, ListFilter } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { adminService } from "@/pagesadmin/adminApi";

// @origin: v2 | @since: 3d9ebf6d, c064a0da | @used-by: ALL | @purpose: 2f port
// -- Stage Analysis table + chart, filterable by BAP/LE and staff-like
// exclusion. Ported verbatim from donor tip (5fe70ff4); backend counterpart
// is los_app.apis.admin_stage_analysis.

interface StageRow {
	stage: string;
	label: string;
	currently_at: number;
	ever_visited: number;
	avg_seconds: number | null;
	avg_display: string;
}

interface StageAnalysisData {
	stages: StageRow[];
	filters: {
		baps: { id: number; name: string }[];
		lenders: { id: number; name: string }[];
	};
}

type SortKey = keyof Pick<StageRow, "label" | "currently_at" | "ever_visited" | "avg_seconds">;

const StageAnalysisPanel: React.FC = () => {
	const [data, setData] = useState<StageAnalysisData | null>(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);

	const [bapId, setBapId] = useState<string>("");
	const [leId, setLeId] = useState<string>("");
	const [excludeStaffLike, setExcludeStaffLike] = useState<boolean>(true);
	const [isFilterExpanded, setIsFilterExpanded] = useState(false);

	const [sortConfig, setSortConfig] = useState<{ key: SortKey; direction: "asc" | "desc" }>({
		key: "ever_visited",
		direction: "desc",
	});

	useEffect(() => {
		if (data?.filters?.baps?.length === 1 && !bapId) {
			setBapId(String(data.filters.baps[0].id));
		}
	}, [data, bapId]);

	useEffect(() => {
		const fetchData = async () => {
			setLoading(true);
			try {
				const params: { bap_id?: string; le_id?: string; exclude_staff_like?: boolean } = {
					exclude_staff_like: excludeStaffLike,
				};
				if (bapId) params.bap_id = bapId;
				if (leId) params.le_id = leId;
				const res = await adminService.getStageAnalysis(params);
				setData(res.data);
				setError(null);
			} catch (err) {
				console.error(err);
				setError("Failed to load stage analysis data");
			} finally {
				setLoading(false);
			}
		};
		fetchData();
	}, [bapId, leId, excludeStaffLike]);

	const handleSort = (key: SortKey) => {
		setSortConfig((prev) => {
			if (prev.key === key) {
				return { key, direction: prev.direction === "asc" ? "desc" : "asc" };
			}
			return { key, direction: "desc" };
		});
	};

	const SortIcon = ({ column }: { column: SortKey }) => {
		if (sortConfig.key !== column)
			return <ArrowUpDown className="h-3.5 w-3.5 text-gray-300 ml-1.5 opacity-0 group-hover:opacity-100 transition-opacity" />;
		return sortConfig.direction === "asc" ? (
			<ArrowUp className="h-3.5 w-3.5 text-intellend-green ml-1.5" />
		) : (
			<ArrowDown className="h-3.5 w-3.5 text-intellend-green ml-1.5" />
		);
	};

	const sortedStages = data
		? [...data.stages].sort((a, b) => {
				const aVal = a[sortConfig.key];
				const bVal = b[sortConfig.key];
				if (aVal === null && bVal === null) return 0;
				if (aVal === null) return 1;
				if (bVal === null) return -1;
				const modifier = sortConfig.direction === "asc" ? 1 : -1;
				if (typeof aVal === "string" && typeof bVal === "string") {
					return aVal.localeCompare(bVal) * modifier;
				}
				return ((aVal as number) < (bVal as number) ? -1 : 1) * modifier;
		  })
		: [];

	const chartData = data
		? data.stages
				.filter((s) => s.avg_seconds !== null && s.avg_seconds > 0)
				.map((s) => ({ name: s.label, seconds: s.avg_seconds as number }))
		: [];

	if (loading && !data) {
		return (
			<div className="flex items-center justify-center h-64">
				<Loader2 className="h-8 w-8 animate-spin text-intellend-green" />
			</div>
		);
	}

	if (error || !data) {
		return (
			<div className="p-4 bg-red-50 text-red-700 rounded-md border border-red-200">
				{error || "No data available"}
			</div>
		);
	}

	return (
		<div className="space-y-6">
			{/* Filter Toggle Button */}
			{!isFilterExpanded && (
				<div className="flex justify-end">
					<Button
						variant="outline"
						size="sm"
						onClick={() => setIsFilterExpanded(true)}
						className="gap-1.5 border-intellend-green/20 text-intellend-green hover:bg-intellend-green/5 hover:border-intellend-green"
					>
						<Filter className="h-4 w-4" />
						Filter Data
					</Button>
				</div>
			)}

			{isFilterExpanded && (
				<div className="bg-white p-6 rounded-xl shadow-md border border-intellend-green/10 relative">
					<Button
						variant="ghost"
						size="icon"
						onClick={() => setIsFilterExpanded(false)}
						className="absolute top-2 right-2 h-8 w-8 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-full transition-colors"
					>
						<X className="h-5 w-5" />
					</Button>

					<div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mt-2">
						<div className="space-y-2">
							<label className="text-sm font-semibold text-gray-700 flex items-center gap-2">
								<ListFilter className="h-4 w-4 text-intellend-green" />
								Filter by BAP Name
							</label>
							<select
								value={bapId}
								onChange={(e) => setBapId(e.target.value)}
								disabled={data.filters.baps.length === 1}
								className="w-full p-2.5 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-intellend-green/20 focus:border-intellend-green transition-all text-sm"
							>
								{data.filters.baps.length > 1 && <option value="">All BAPs</option>}
								{data.filters.baps.map((bap) => (
									<option key={bap.id} value={bap.id}>
										{bap.name}
									</option>
								))}
							</select>
						</div>
						<div className="space-y-2">
							<label className="text-sm font-semibold text-gray-700 flex items-center gap-2">
								<ListFilter className="h-4 w-4 text-intellend-green" />
								Filter by Lending Entity (LE)
							</label>
							<select
								value={leId}
								onChange={(e) => setLeId(e.target.value)}
								className="w-full p-2.5 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-intellend-green/20 focus:border-intellend-green transition-all text-sm"
							>
								<option value="">All Lenders</option>
								{data.filters.lenders.map((le) => (
									<option key={le.id} value={le.id}>
										{le.name}
									</option>
								))}
							</select>
						</div>
					</div>

					<div className="flex items-center gap-2 mt-6 pt-4 border-t border-gray-100">
						<Checkbox
							id="exclude-staff-like"
							checked={excludeStaffLike}
							onCheckedChange={(checked) => setExcludeStaffLike(checked === true)}
						/>
						<label
							htmlFor="exclude-staff-like"
							className="text-sm font-medium text-gray-700 cursor-pointer select-none"
						>
							Exclude staff / superuser accounts (and any customer accounts sharing a staff member's name)
						</label>
					</div>
				</div>
			)}

			{/* Table */}
			<Card className="border-intellend-green/10 shadow-sm overflow-hidden relative">
				{loading && (
					<div className="absolute inset-0 bg-white/50 backdrop-blur-[1px] flex items-center justify-center z-10">
						<Loader2 className="h-6 w-6 animate-spin text-intellend-green" />
					</div>
				)}
				<CardHeader className="pb-3">
					<CardTitle className="text-lg font-bold text-gray-900">Journey Stage Breakdown</CardTitle>
					<CardDescription>
						Users currently at each stage, users who have ever visited it, and the average time spent there.
					</CardDescription>
				</CardHeader>
				<CardContent className="p-0">
					<Table>
						<TableHeader>
							<TableRow className="bg-gray-50">
								<TableHead className="font-semibold text-gray-700 group cursor-pointer" onClick={() => handleSort("label")}>
									<div className="flex items-center">
										Stage <SortIcon column="label" />
									</div>
								</TableHead>
								<TableHead className="font-semibold text-gray-700 text-right group cursor-pointer" onClick={() => handleSort("currently_at")}>
									<div className="flex items-center justify-end">
										Currently At <SortIcon column="currently_at" />
									</div>
								</TableHead>
								<TableHead className="font-semibold text-gray-700 text-right group cursor-pointer" onClick={() => handleSort("ever_visited")}>
									<div className="flex items-center justify-end">
										Ever Visited <SortIcon column="ever_visited" />
									</div>
								</TableHead>
								<TableHead className="font-semibold text-gray-700 text-right group cursor-pointer" onClick={() => handleSort("avg_seconds")}>
									<div className="flex items-center justify-end">
										Avg Time Spent <SortIcon column="avg_seconds" />
									</div>
								</TableHead>
							</TableRow>
						</TableHeader>
						<TableBody>
							{sortedStages.length === 0 ? (
								<TableRow>
									<TableCell colSpan={4} className="text-center py-12 text-gray-400">
										No stage data yet.
									</TableCell>
								</TableRow>
							) : (
								sortedStages.map((s) => (
									<TableRow key={s.stage} className="hover:bg-gray-50/60 transition-colors">
										<TableCell className="font-medium text-gray-900">
											{s.label}
											<div className="text-[10px] font-mono text-gray-400">{s.stage}</div>
										</TableCell>
										<TableCell className="text-right font-mono">{s.currently_at}</TableCell>
										<TableCell className="text-right font-mono">{s.ever_visited}</TableCell>
										<TableCell className="text-right font-mono text-intellend-green font-semibold">{s.avg_display}</TableCell>
									</TableRow>
								))
							)}
						</TableBody>
					</Table>
				</CardContent>
			</Card>

			{/* Chart */}
			{chartData.length > 0 && (
				<Card className="border-intellend-green/10 shadow-sm">
					<CardHeader className="pb-3">
						<CardTitle className="text-base font-semibold text-gray-900">Average Time Spent per Stage (seconds)</CardTitle>
					</CardHeader>
					<CardContent className="h-[400px]">
						<ResponsiveContainer width="100%" height="100%">
							<BarChart data={chartData} layout="vertical" margin={{ left: 20, right: 20 }}>
								<CartesianGrid strokeDasharray="3 3" horizontal={false} />
								<XAxis type="number" />
								<YAxis type="category" dataKey="name" width={180} tick={{ fontSize: 11 }} />
								<Tooltip
									formatter={(value: number) => [`${value}s`, "Avg Time"]}
									contentStyle={{ borderRadius: "8px", border: "none", boxShadow: "0 4px 12px rgba(0,0,0,0.1)" }}
								/>
								<Bar dataKey="seconds" fill="#10b981" radius={[0, 4, 4, 0]} />
							</BarChart>
						</ResponsiveContainer>
					</CardContent>
				</Card>
			)}
		</div>
	);
};

export default StageAnalysisPanel;
